import React, { useState, useEffect } from "react";
import axios from "../../../../axios";
import { errorToast } from "../../../../utils/toast";
import { motion } from "framer-motion";
import { useUser } from "../../../../hooks/use-user";
import { Link, useLocation } from "react-router-dom";
import {
  Trophy,
  Medal,
  Crown,
  Target,
  Clock,
  CheckCircle,
  XCircle,
  Zap,
  ChevronRight,
  Award,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";

const WinnersPage = () => {
  const { data: user } = useUser();
  const location = useLocation();
  const contestIdFromState = location.state?.contestId;

  const [countData, setCountData] = useState(null);
  const [reportsData, setReportsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const activeContestId = contestIdFromState;
      if (!activeContestId) return;

      setLoading(true);
      try {
        const [detailsRes, reportRes] = await Promise.all([
          axios.get("contestDetails", {
            params: { contestId: activeContestId },
          }),
          axios.get("getContestReport", {
            params: { contestId: activeContestId },
          }),
        ]);

        const details = detailsRes?.data?.result;
        if (details) {
          setCountData({
            rightCount: details.totalRightAnswerCount,
            wrongCount: details.totalWrongAnswerCount,
            totalAttempts: details.totalAttempts || 0,
            accuracy: details.accuracy || 0,
            totalTimeTaken: details.totalTimeTaken || 0,
            name: details.name,
            contestId: details.contestId,
            contestName: details.contestName,
            totalContestSlot: details.totalContestSlot,
            userRank: details.userRank,
            winningAmount: details.winningAmount,
            totalUserContestJoinedCount: details.totalUserContestJoinedCount,
          });
        }

        const report = reportRes?.data?.result;
        if (report) {
          setReportsData(report);
        }
      } catch (err) {
        errorToast(err?.response?.data?.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    if (user || contestIdFromState) {
      fetchData();
    }
  }, [user, contestIdFromState]);

  const formatTime = (seconds) => {
    if (!seconds) return "0s";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const getRankBadge = (rank) => {
    if (rank === 1)
      return {
        icon: Crown,
        color: "from-yellow-400 to-amber-500",
        bg: "bg-yellow-500/20",
        text: "text-yellow-400",
        label: "🥇",
      };
    if (rank === 2)
      return {
        icon: Medal,
        color: "from-gray-300 to-gray-400",
        bg: "bg-gray-400/20",
        text: "text-gray-300",
        label: "🥈",
      };
    if (rank === 3)
      return {
        icon: Award,
        color: "from-amber-600 to-amber-700",
        bg: "bg-amber-600/20",
        text: "text-amber-500",
        label: "🥉",
      };
    return {
      icon: Trophy,
      color: "from-purple-500 to-indigo-500",
      bg: "bg-purple-500/10",
      text: "text-purple-400",
      label: `#${rank}`,
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#2A1B3D] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 text-sm">Loading results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#2A1B3D] pb-20 pt-10">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-transparent to-indigo-600/20"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-5xl mx-auto px-4 pt-8 pb-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-6"
          >
            <div className="inline-flex items-center gap-2 bg-purple-500/20 px-4 py-1.5 rounded-full mb-3">
              <Trophy size={16} className="text-yellow-400" />
              <span className="text-purple-200 text-sm font-medium">
                Contest Results
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
              {countData?.contestName || "Contest"}
            </h1>
            <p className="text-gray-400 text-sm">
              {countData?.totalUserContestJoinedCount || 0} participants
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 flex flex-col lg:flex-row gap-6">
        {/* Left — Personal Stats */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:w-[380px] flex-shrink-0"
        >
          {/* Rank & Prize Card */}
          <div className="bg-[#3F234E] rounded-2xl border border-white/5 overflow-hidden mb-4">
            <div className="bg-gradient-to-r from-purple-600/30 to-indigo-600/30 p-6 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 mb-3 shadow-lg shadow-purple-500/30">
                {countData?.userRank <= 3 ? (
                  <Crown size={36} className="text-yellow-300" />
                ) : (
                  <span className="text-3xl font-black text-white">
                    #{countData?.userRank || "-"}
                  </span>
                )}
              </div>
              {countData?.userRank <= 3 && (
                <p className="text-4xl font-black text-white mb-1">
                  #{countData?.userRank}
                </p>
              )}
              <h2 className="text-lg font-bold text-white">
                {countData?.name}
              </h2>
              <p className="text-gray-400 text-sm mt-0.5">
                Rank {countData?.userRank} of{" "}
                {countData?.totalUserContestJoinedCount}
              </p>
            </div>

            {/* Prize */}
            {countData?.winningAmount > 0 && (
              <div className="mx-4 -mt-3 mb-4">
                <div className="bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-500/30 rounded-xl p-4 text-center">
                  <p className="text-yellow-300 text-xs font-medium uppercase tracking-wider mb-1">
                    Prize Won
                  </p>
                  <p className="text-2xl font-black text-yellow-400">
                    {countData?.winningAmount}{" "}
                    <span className="text-lg">coins</span>
                  </p>
                </div>
              </div>
            )}

            {/* Stats Grid */}
            <div className="p-4 pt-0">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3 text-center">
                  <CheckCircle
                    size={20}
                    className="text-green-400 mx-auto mb-1"
                  />
                  <p className="text-xl font-bold text-green-400">
                    {countData?.rightCount || 0}
                  </p>
                  <p className="text-gray-400 text-xs">Correct</p>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-center">
                  <XCircle size={20} className="text-red-400 mx-auto mb-1" />
                  <p className="text-xl font-bold text-red-400">
                    {countData?.wrongCount || 0}
                  </p>
                  <p className="text-gray-400 text-xs">Wrong</p>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-center">
                  <Target size={18} className="text-blue-400 mx-auto mb-1" />
                  <p className="text-xl font-bold text-blue-400">
                    {countData?.accuracy || 0}%
                  </p>
                  <p className="text-gray-400 text-xs">Accuracy</p>
                </div>
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3 text-center">
                  <Clock size={18} className="text-purple-400 mx-auto mb-1" />
                  <p className="text-xl font-bold text-purple-400">
                    {formatTime(countData?.totalTimeTaken)}
                  </p>
                  <p className="text-gray-400 text-xs">Time</p>
                </div>
              </div>
            </div>

            {/* Wallet Button */}
            <div className="p-4 pt-0">
              <Link to="/dash/wallet" className="block">
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-3.5 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-95 transition-all shadow-lg shadow-purple-500/20">
                  <Wallet size={18} className="text-white" />
                  <span className="text-white font-semibold text-sm">
                    Go to Wallet
                  </span>
                  <ChevronRight size={16} className="text-white/60" />
                </div>
              </Link>
            </div>
          </div>

          {/* Ranking Info Card */}
          <div className="bg-[#3F234E] rounded-2xl border border-white/5 p-4">
            <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
              <Zap size={14} className="text-yellow-400" /> Ranking Rules
            </h3>
            <div className="space-y-2">
              {[
                { num: "1", text: "Higher Score wins" },
                { num: "2", text: "Higher Accuracy (if tied)" },
                { num: "3", text: "Less Time Taken (if tied)" },
                { num: "4", text: "Earlier Submission (if tied)" },
              ].map((rule) => (
                <div key={rule.num} className="flex items-start gap-2.5">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-500/30 text-purple-300 text-xs flex items-center justify-center font-bold">
                    {rule.num}
                  </span>
                  <span className="text-gray-400 text-xs leading-5">
                    {rule.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Right — Leaderboard */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="flex-1 min-w-0"
        >
          <div className="bg-[#3F234E] rounded-2xl border border-white/5 overflow-hidden">
            {/* Leaderboard Header */}
            <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp size={18} className="text-purple-400" />
                <h2 className="text-white font-bold text-lg">Leaderboard</h2>
              </div>
              <div className="flex items-center gap-1.5 bg-white/5 px-3 py-1 rounded-full">
                <Users size={14} className="text-gray-400" />
                <span className="text-gray-400 text-xs font-medium">
                  Top 10
                </span>
              </div>
            </div>

            {/* Column Headers */}
            <div className="px-5 py-2.5 bg-white/[0.02] border-b border-white/5 grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-1">#</div>
              <div className="col-span-3">Player</div>
              <div className="col-span-2 text-center">Score</div>
              <div className="col-span-2 text-center hidden sm:block">
                Accuracy
              </div>
              <div className="col-span-2 text-center hidden sm:block">Time</div>
              <div className="col-span-2 text-right">Prize</div>
            </div>

            {/* Entries */}
            <div className="divide-y divide-white/5">
              {reportsData.length === 0 ? (
                <div className="px-5 py-16 text-center">
                  <Clock
                    size={48}
                    className="text-purple-500/50 mx-auto mb-4 animate-pulse"
                  />
                  <h3 className="text-xl font-bold text-white mb-2">
                    Results Processing
                  </h3>
                  <p className="text-gray-400 text-sm max-w-sm mx-auto mb-6">
                    The contest has ended and answers are being finalized.
                    Please wait a moment while we calculate the official
                    rankings and distribute prizes.
                  </p>
                  <button
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white px-6 py-2.5 rounded-xl font-medium transition-colors"
                  >
                    Refresh Results
                  </button>
                </div>
              ) : (
                reportsData.map((entry, index) => {
                  const rank = index + 1;
                  const badge = getRankBadge(rank);
                  const isCurrentUser =
                    entry?.user?._id === user?._id || entry?._id === user?._id;
                  const accuracyPercent = entry.contestAccuracy || 0;

                  return (
                    <motion.div
                      key={entry._id || index}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`px-5 py-3.5 grid grid-cols-12 gap-2 items-center transition-colors
                        ${
                          isCurrentUser
                            ? "bg-purple-500/10 border-l-2 border-l-purple-500"
                            : "hover:bg-white/[0.02]"
                        }
                        ${rank <= 3 ? "bg-white/[0.02]" : ""}
                      `}
                    >
                      {/* Rank */}
                      <div className="col-span-1">
                        {rank <= 3 ? (
                          <span className="text-xl">{badge.label}</span>
                        ) : (
                          <span className={`text-sm font-bold ${badge.text}`}>
                            {rank}
                          </span>
                        )}
                      </div>

                      {/* Player */}
                      <div className="col-span-3 min-w-0">
                        <p
                          className={`text-sm font-semibold truncate ${isCurrentUser ? "text-purple-300" : "text-white"}`}
                        >
                          {entry?.user?.name || "Unknown"}
                          {isCurrentUser && (
                            <span className="text-[10px] text-purple-400 ml-1 font-normal">
                              (You)
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Score */}
                      <div className="col-span-2 text-center">
                        <span className="text-sm font-bold text-green-400">
                          {entry.score || 0}
                        </span>
                      </div>

                      {/* Accuracy */}
                      <div className="col-span-2 text-center hidden sm:block">
                        <span
                          className={`text-sm font-medium ${accuracyPercent >= 70 ? "text-green-400" : accuracyPercent >= 40 ? "text-yellow-400" : "text-red-400"}`}
                        >
                          {accuracyPercent}%
                        </span>
                      </div>

                      {/* Time */}
                      <div className="col-span-2 text-center hidden sm:block">
                        <span className="text-sm text-gray-400">
                          {formatTime(entry.timeTaken)}
                        </span>
                      </div>

                      {/* Prize */}
                      <div className="col-span-2 text-right">
                        {entry.prizeWon > 0 ? (
                          <span className="inline-flex items-center gap-1 bg-yellow-500/15 text-yellow-400 px-2 py-0.5 rounded-full text-xs font-bold">
                            {entry.prizeWon}
                          </span>
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WinnersPage;
